import { useCallback, useEffect, useState } from 'react';
import {
  ApiError,
  type ScenarioRecord,
  getScenarios,
  reactivateScenario,
  retireScenario,
} from './api';
import {
  type ScenarioPending,
  type ScenarioPendingAction,
  describeScenarioActionError,
} from './scenarioTruth';

/**
 * The scenarios management screen's data hook — the same shape useSettings.ts
 * uses: a GET-on-mount read of what the screen renders, plus the actions it
 * offers, with the in-flight marker tracked separately from the load state so
 * one row's action never blanks the list.
 *
 * REFETCH, not optimistic. Deliberate, and not a shortcut: the retire/
 * reactivate routes return the RAW search_scenarios row — they do not re-run
 * the staleness derivation — so their response cannot tell us whether the
 * scenario is servable now. A reactivated scenario that still contradicts the
 * profile stays retired-stale by derivation, and an optimistic "it's back!"
 * would have been a lie on screen. Re-reading GET /scenarios (the one endpoint
 * that DOES derive) is the only way this surface can state the truth. It also
 * matches CorpusArchivePanel's own onChanged discipline: after a write, ask the
 * server what is true rather than guessing locally.
 *
 * NO backend changes — every call hits a route R386 already shipped (see
 * api.ts's per-function route+line citations).
 */

export interface ScenariosState {
  scenarios: ScenarioRecord[];
  loading: boolean;
  /** The LIST read's own failure (never merged with an action failure — the
   *  same per-source separation useSettings.LoadState documents). */
  error: string | null;
}

const INITIAL_STATE: ScenariosState = { scenarios: [], loading: true, error: null };

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return err instanceof Error ? err.message : String(err);
}

export function useScenarios() {
  const [state, setState] = useState<ScenariosState>(INITIAL_STATE);
  /** Which row has an action in flight, and which action — so the panel greys
   *  exactly that row and leaves the rest usable. */
  const [pending, setPending] = useState<ScenarioPending | null>(null);
  /** The last action failure, already in Meira's voice via the pure mapper.
   *  Cleared when a new action starts — a stale error must never sit under a
   *  succeeded action. */
  const [actionError, setActionError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const result = await getScenarios();
      setState({ scenarios: result.scenarios ?? [], loading: false, error: null });
    } catch (err) {
      // The list stays as-is on a refresh failure (a transient error must not
      // wipe rows the user is looking at) — the error surfaces instead.
      setState((s) => ({ ...s, loading: false, error: errorMessage(err) }));
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const run = useCallback(
    async (id: string, action: ScenarioPendingAction, call: () => Promise<unknown>) => {
      setPending({ id, action });
      setActionError(null);
      try {
        await call();
        await refresh();
      } catch (err) {
        setActionError(describeScenarioActionError(action, errorMessage(err)));
      } finally {
        setPending(null);
      }
    },
    [refresh],
  );

  /** POST /scenarios/:id/retire. `reason` is the user's own note when a caller
   *  collects one; this screen sends none rather than inventing one. */
  const retire = useCallback(
    (id: string, reason?: string) => run(id, 'retire', () => retireScenario(id, reason)),
    [run],
  );

  const reactivate = useCallback(
    (id: string) => run(id, 'reactivate', () => reactivateScenario(id)),
    [run],
  );

  return {
    scenarios: state.scenarios,
    loading: state.loading,
    error: state.error,
    pending,
    actionError,
    refresh,
    retire,
    reactivate,
  };
}
